import styles from "./Worker.module.css";
import * as React from "react";
import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import { DataGrid, GridToolbarFilterButton } from "@mui/x-data-grid";
import {
  addNewWorkerToShift,
  alignTimeOfWorkersWithJob,
  changeWorkerRowColorIdentifier,
  doorToDoorPolicy,
  GetJobWithWorkersModel,
  getPaymentMethod,
  getSignedInOptions,
  getWorkersFromJob,
  GetWorkersSuggestions,
  getWorkRolesSuggestionsV2,
  removeWorkersFromJob,
  saveJobVersionHistory,
  sendPushNotifications,
  updateJobWorkerRows,
} from "../../../../../api/apiCall";
import { useAuth } from "../../../../../auth/auth";
import {
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import AppModal from "../../../../../components/AppModal/AppModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faX,
  faEnvelope,
  faPlus,
  faBusinessTime,
  faBan,
  faUserShield,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { getFormattedDiffs } from "../../../../../utils/diffUtils";
import { CHECK_TYPES } from "../../../../../constants/constants";
import { getHColor, getSColor, getTColor } from "../../../../../utils/statusUtils";
import { calculateHours } from "../../../../../utils/utils";
import { format, parseISO, set } from "date-fns";
import { createEmptyWorker, getBackColor, saveChangesHistory, shiftTypeRates, updateRows } from "../../../../../utils/workersUtils";
import CancellationModal from "./CancelReason/CancellationSelector";

const Workers = ({ id, week, year, shiftData, connection }: any) => {
  const { state } = useAuth();
  const [workers, setWorkers] = useState<any>([]);
  const [oldWorkers, setOldWorkers] = useState<any>([]);
  const [signedInOptions, setSignedInOptions] = useState<any>([]);
  const [selectedRows, setSelectedRows] = useState<any>([]);
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState<any>([]);
  const [workRoleSuggestions, setWorkRoleSuggestions] = useState<any>([]);
  const [biggestOrderNumber, setBiggestOrderNumber] = useState<any>();
  const [selectedColor, setSelectedColor] = useState("");
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [loadingSpin, setLoadingSpin] = useState(false);
  const [data, setData] = useState({ userJobId: null, workerId: null });
  const [openCancelReasonModal, setOpenCancelReasonModal] = useState(false);
  const typingDelay = 100;
  let typingTimer: any;

  const handleChange = async (
    colId: any,
    field: any,
    newValue: any,
    workRoleId?: any
  ) => {
    const updatedRows = updateRows(colId, field, newValue, workers, workRoleId);
    setWorkers(updatedRows);

    const normalizeWorker = (worker: any) => ({
      ...worker,
      h: Number(worker.h),
      hourlyRate: Number(worker.hourlyRate),
    });

    const normalizedNew = updatedRows.map(normalizeWorker);

    const formattedDiffs = getFormattedDiffs(oldWorkers, normalizedNew);
    if (!formattedDiffs) {
      return;
    }

    setLoadingSpin(true);

    const rowToUpdate = updatedRows.find(
      (row: any) => row.userJobId === colId
    );

    const response = await updateJobWorkerRows(state.user?.token, [rowToUpdate]);

    if (response) {
      if (field === "postCode") {
        const doorPolicy = await doorToDoorPolicy(
          state.user?.token,
          colId,
          calculateHours(
            format(shiftData.startTime, "HH:mm"),
            format(shiftData.endTime, "HH:mm")
          )
        );

        if (doorPolicy.isOverLimit) {
          toast.error(doorPolicy.message);

          const updatedNotes = updatedRows.map((row: any) =>
            row.userJobId === colId
              ? {
                  ...row,
                  notes:
                    row.notes && row.notes !== "null"
                      ? row.notes.replace(/\(Door.*?m\)/g, "").trim() +
                        " " +
                        doorPolicy.message
                      : doorPolicy.message,
                  colorIdentifier: "#f28519",
                }
              : row
          );

          setWorkers(updatedNotes);

          const notePayload = {
            ...updatedNotes.find((row: any) => row.userJobId === colId),
            ...(workRoleId && { workRoleId }),
          };

          await updateJobWorkerRows(state.user?.token, [notePayload]);
        } else if (
          doorPolicy?.response?.data === "ERROR!" ||
          doorPolicy?.response?.data ===
            "Please make sure PostCodes are valid!"
        ) {
          toast.error("Invalid Postcode");

          const updatedNotes = updatedRows.map((row: any) =>
            row.userJobId === colId
              ? {
                  ...row,
                  notes:
                    row.notes && row.notes !== "null"
                      ? row.notes.replace(/\(Door.*?m\)/g, "").trim()
                      : null,
                  colorIdentifier: "white",
                }
              : row
          );

          setWorkers(updatedNotes);

          const notePayload = {
            ...updatedNotes.find((row: any) => row.userJobId === colId),
            ...(workRoleId && { workRoleId }),
          };

          await updateJobWorkerRows(state.user?.token, [notePayload]);
        } else {
          const updatedNotes = updatedRows.map((row: any) =>
            row.userJobId === colId
              ? {
                  ...row,
                  notes:
                    row.notes && row.notes !== "null"
                      ? row.notes.replace(/\(Door.*?m\)/g, "").trim()
                      : null,
                  colorIdentifier: "white",
                }
              : row
          );

          setWorkers(updatedNotes);

          const notePayload = {
            ...updatedNotes.find((row: any) => row.userJobId === colId),
            ...(workRoleId && { workRoleId }),
          };

          await updateJobWorkerRows(state.user?.token, [notePayload]);
        }
      }

      setOldWorkers(updatedRows);

      setTimeout(() => {
        setLoadingSpin(false);
      }, 350);

      await saveChangesHistory(state.user?.token, id, formattedDiffs);

      const response = await GetJobWithWorkersModel(state.user?.token, id);

      try {
        await connection.invoke("EditJob", `${week}-${year}`, response);
      } catch (error) {
        console.error("Error invoking EditJob:", error);
      }
    }
  };

  const columns: any = [
    {
      field: "orderNumber",
      headerName: "#",
      width: 80,
      filterable: false,
      headerAlign: "center",
      renderCell: (params: any) => (
        <TextField
          type="number"
          size="small"
          defaultValue={params.row.orderNumber}
          style={{ width: "65px", height: "5px", margin: 5 }}
          onChange={(event: any) => {
            handleChange(params.id, "orderNumber", event.target.value);
          }}
        />
      )
    },
    {
      field: "workRole",
      headerName: "Role",
      width: 250,
      renderCell: (params: any) => (
        <Autocomplete
          size="small"
          sx={{ m: 0.5 }}
          options={workRoleSuggestions}
          getOptionLabel={(option) => option.workRoleName}
          isOptionEqualToValue={(option, value) =>
            option.workRoleName === value.workRoleName
          }
          value={
            workRoleSuggestions.find(
              (role: any) => role.workRoleName === params.row.workRole
            ) || null
          }
          onChange={(_, value) => {
            handleChange(params.id, "workRole", value?.workRoleName || "", value?.id || null);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              inputProps={{
                ...params.inputProps,
                onKeyDown: (event) => {
                  if (["ArrowUp", "ArrowDown", " "].includes(event.key)) {
                    event.stopPropagation();
                  }
                },
              }}
            />
          )}
        />
      ),
    },
    {
      field: "payRate",
      headerName: "Pay Rate",
      width: 100,
      editable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params: any) => (
        <TextField
          type="number"
          size="small"
          defaultValue={params.row.payRate}
          style={{ width: "80px", height: "5px", margin: 5 }}
          onBlur={(event: any) => {
            handleChange(params.id, "payRate", event.target.value);
          }}
        />
      )
    },
    {
      field: "chargeRate",
      headerName: "Charge Rate",
      width: 100,
      editable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params: any) => {
        return (
          <div style={{ fontWeight: "bold" }}>
            {
              params.row.chargeRate
            }
          </div>
        )
      }
    },
    {
      field: "margin",
      headerName: "Margin",
      width: 70,
      editable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params: any) => {
        return (
          <div style={{ fontWeight: "bold" }}>
            {params.row.margin != null
              ? `${params.row.margin}%`
              : ""}
          </div>
        )
      }
    },
    {
      field: "fullName",
      headerName: "Full Name",
      width: 250,
      renderCell: (params: any) => (
        <Autocomplete
          key={`autocomplete-${params.id}`}
          size="small"
          value={params.row || null}
          getOptionDisabled={(option) => option.isBusy || option.inefficientRest || option.isOnAnnualLeave}
          options={suggestions || []}
          disableClearable={false}
          style={{ margin: 5 }}
          getOptionLabel={(option: any) => `${option?.fullName || ""}`}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          onChange={(event, newValue) => {
            if (params.row.fullName === null && newValue === null) return;
            handleNameChange(params.id, newValue || null, params.row.userId);
          }}
          renderOption={(props, option) => (
            <ListItem
              {...props}
              sx={{
                backgroundColor: option.isBusy || option.isOnAnnualLeave
                  ? '#ff0000 !important' 
                  : option.inefficientRest 
                    ? '#ffa500 !important'
                    : 'inherit',
                opacity: option.isBusy || option.inefficientRest ||option.isOnAnnualLeave ? '0.8 !important' : 1 ,
                color: (option.isBusy || option.inefficientRest || option.isOnAnnualLeave) ? 'white' : 'black',
              }}
            >
              <ListItemText
                primary={option.fullName}
                secondary={
                  option.isOnAnnualLeave
                    ? 'Annual Leave'
                    : option.isBusy
                      ? 'Busy'
                      : option.inefficientRest
                        ? 'Insufficient Rest'
                        : option.pts != null
                          ? `PTS: ${option.pts}`
                          : null
                }
                primaryTypographyProps={{
                  sx: { 
                    color: (option.isBusy || option.inefficientRest || option.isOnAnnualLeave) ? 'white' : 'black' 
                  },
                }}
                secondaryTypographyProps={{
                  sx: { 
                    color: (option.isBusy || option.inefficientRest || option.isOnAnnualLeave) ? 'white' : 'black' 
                  },
                }}
              />
            </ListItem>
          )}
          renderInput={(params) =>
            <TextField
              {...params}
              inputProps={{
                ...params.inputProps,
                onKeyDown: (event) => {
                  if (event.key === ' ') {
                    event.stopPropagation();
                  }
                },
              }}
            />
          }
        />
      )
    },
    {
      field: "pts",
      headerName: "PTS",
      width: 80,
    },
    {
      field: "phoneNumber",
      headerName: "Phone Number",
      width: 120,
    },
    {
      field: "postCode",
      headerName: "Post Code",
      width: 150,
      headerAlign: "center",
      renderCell: (params: any) => (
        <TextField
          type="text"
          size="small"
          value={params.row.postCode || ""}
          style={{ width: "130px", height: "5px", margin: 5 }}
          onChange={(event: any) => {
            const updatedRows = updateRows(params.id, 'postCode', event.target.value, workers);
            setWorkers(updatedRows);
          }}
          onBlur={async (event: any) => {
            handleChange(params.id, "postCode", event.target.value.trim());
          }}
          inputProps={{
            onKeyDown: (event) => {
              if (["ArrowUp", "ArrowDown"].includes(event.key)) {
                event.stopPropagation();
              }
              if (event.key === ' ') {
                event.stopPropagation();
              }
            },
          }}
        />
      ),
    },
    {
      field: "t",
      headerName: "T",
      width: 80,
      headerAlign: "center",
      renderCell: (params: any) => (
        <Select
          labelId="demo-select-small-label"
          id="demo-select-small"
          style={{ height: "25px", width: "60px", backgroundColor: getTColor(params.row.t) }}
          value={params.row.t}
          onChange={(event, newValue: any) => {
            handleChange(params.id, "t", newValue?.props?.value);
          }}
        >
          <MenuItem value={0} style={{ height: 25 }}></MenuItem>
          <MenuItem
            value={1}
            style={{ backgroundColor: "#90ee90", height: 25 }}
          >
            <FontAwesomeIcon icon={faCheck} />
          </MenuItem>
          <MenuItem value={2} style={{ backgroundColor: "red", height: 25 }}>
            <FontAwesomeIcon icon={faX} />
          </MenuItem>
        </Select>
      ),
    },
    {
      field: "s",
      headerName: "S",
      width: 80,
      headerAlign: "center",
      renderCell: (params: any) => (
        <Select
          labelId="demo-select-small-label"
          id="demo-select-small"
          style={{ height: "25px", width: "60px", backgroundColor: getSColor(params.row.s) }}
          value={params.row.s}
          onChange={(event, newValue: any) => {
            handleChange(params.id, "s", newValue?.props?.value);
          }}
        >
          <MenuItem value={0} style={{ height: 25 }}></MenuItem>
          <MenuItem
            value={1}
            style={{ backgroundColor: "#90ee90", height: 25 }}
          >
            <FontAwesomeIcon icon={faCheck} />
          </MenuItem>
          <MenuItem value={2} style={{ backgroundColor: "red", height: 25 }}>
            <FontAwesomeIcon icon={faX} />
          </MenuItem>
          <MenuItem
            value={3}
            style={{ backgroundColor: "#f7de3a", height: 25 }}
          >
            <FontAwesomeIcon icon={faEnvelope} />
          </MenuItem>
          <MenuItem
            value={4}
            style={{ backgroundColor: "#e0e0e0", height: 25 }}
          >
           <FontAwesomeIcon icon={faBan} />
          </MenuItem>
          <MenuItem
            value={5}
            style={{ backgroundColor: "#5bc0de", height: 25 }}
          >
            <FontAwesomeIcon icon={faUserShield} />
          </MenuItem>
        </Select>
      ),
    },
    {
      field: "h_color",
      headerName: "H",
      width: 110,
      headerAlign: "center",
      renderCell: (params: any) => (
        <Select
          labelId="demo-select-small-label"
          id="demo-select-small"
          style={{ height: "25px", width: "100px", backgroundColor: getHColor(params.row.h_Color) }}
          value={params.row.h_Color}
          onChange={(event, newValue: any) => {
            handleChange(params.id, "h_Color", newValue?.props?.value);
          }}
        >
          {signedInOptions && Array.isArray(signedInOptions) && signedInOptions.map((options: any) => (
            <MenuItem
              value={options.value}
              style={{
                backgroundColor: getHColor(options.value),
                height: 25,
              }}
            >
              {options.name === "Neutral" ? "" : options.name}
            </MenuItem>
          ))}
        </Select>
      ),
    },
    {
      field: "fatigueIndex",
      headerName: "F",
      width: 80,
      editable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params: any) => {
        return (
          <div style={{ fontWeight: "bold" }}>
            {params.row.isTraining ? (
              <FontAwesomeIcon icon={faBusinessTime} />
            ) :
              params.row.fatigueIndex?.toFixed(1)
            }
          </div>
        )
      }
    },
    {
      field: "RiskIndex",
      headerName: "R",
      width: 80,
      editable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params: any) => {
        return (
          <div style={{ fontWeight: "bold" }}>
            {params.row.isTraining ? (
              <FontAwesomeIcon icon={faBusinessTime} />
            ) :
              params.row.riskIndex?.toFixed(1)
            }
          </div>
        )
      }
    },
    {
      field: "d",
      headerName: "D",
      width: 40,
      editable: false,
      headerAlign: "center",
      renderCell: (params: any) => (
        <Checkbox
          checked={params.row.d}
          style={{ color: params.row.d && '#74c365' }}
          onChange={(event, checked) => {
            handleChange(params.id, "d", checked);
          }}
        />
      ),
    },
    {
      field: "paymentMethodId",
      headerName: "P",
      width: 120,
      headerAlign: "center",
      renderCell: (params: any) => (
        <Select
          labelId="demo-select-small-label"
          id="demo-select-small"
          style={{ height: "25px", width: "110px" }}
          value={params.row.paymentMethodId}
          onChange={(event, newValue: any) => {
            const selectedValue = newValue?.props?.value === 1 ? null : newValue?.props?.value;
            handleChange(params.id, "paymentMethodId", selectedValue);
          }}
        >
          <MenuItem value={1} style={{ height: 40 }}>
            No payment method
          </MenuItem>
          {paymentMethods.map((options: any) => (
            <MenuItem
              value={options.id}
              style={{ height: 40 }}
            >
              {options.paymentMethodName}
            </MenuItem>
          ))}
        </Select>
      ),
    },
    {
      field: "paid",
      headerName: "Paid",
      width: 40,
      editable: false,
      headerAlign: "center",
      renderCell: (params: any) => (
        <Checkbox
          checked={params.row.paid}
          style={{ color: params.row.paid && '#74c365' }}
          onChange={(event, checked) => {
            handleChange(params.id, "paid", checked);
          }}
        />
      ),
    },
    {
      field: "riskAssessments",
      headerName: "Risk Assessments",
      width: 227,
      headerAlign: "center",
      align: 'center',
      renderCell: (params: any) => (
        <Select
          labelId="demo-select-small-label"
          id="demo-select-small"
          style={{ height: "25px", width: "207px" }}
          value={params.row.riskAssessments}
          onChange={(event) => handleChange(params.id, "riskAssessments", event.target.value)}
        >
          <MenuItem value={0} style={{ height: 25 }}></MenuItem>
          <MenuItem
            value={1}
            style={{ backgroundColor: "#f23819", height: 25 }}
          >
            Risk Assessment Needed
          </MenuItem>
          <MenuItem value={2} style={{ backgroundColor: "#f7de3a", height: 25 }}>
            Risk Assessment Waiting Approval
          </MenuItem>
          <MenuItem
            value={3}
            style={{ backgroundColor: "#90ee90", height: 25 }}
          >
            Risk Assessment Approved
          </MenuItem>
        </Select>
      ),
    },
    // {
    //   field: "h",
    //   headerName: "Actual Hours",
    //   width: 110,
    //   headerAlign: "center",
    //   renderCell: (params: any) => (
    //     <TextField
    //       type="number"
    //       size="small"
    //       defaultValue={params.row.h}
    //       style={{ width: "90px", height: "5px", margin: 5 }}
    //       onChange={(event: any) => {
    //         const updatedRows = updateRows(params.id, 'h', event.target.value, workers);
    //         setWorkers(updatedRows);
    //       }}
    //       onBlur={async (event: any) => {
    //         handleChange(params.id, "h", event.target.value);
    //       }}
    //     />
    //   ),
    // },
    // {
    //   field: "hourlyRate",
    //   headerName: "Hourly Rate",
    //   width: 110,
    //   renderCell: (params: any) => (
    //     <TextField
    //       type="number"
    //       size="small"
    //       defaultValue={params.row.hourlyRate}
    //       style={{ width: "90px", height: "5px", margin: 5 }}
    //       InputProps={{ startAdornment: "£" }}
    //       onChange={(event: any) => {
    //         const updatedRows = updateRows(params.id, 'hourlyRate', event.target.value, workers);
    //         setWorkers(updatedRows);
    //       }}
    //       onBlur={async (event: any) => {
    //         handleChange(params.id, "hourlyRate", event.target.value);
    //       }}
    //     />
    //   ),
    // },
    {
      field: "isTraining",
      headerName: "Training",
      width: 100,
      editable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params: any) => (
        <Checkbox
          checked={params.row.isTraining}
          style={{ color: params.row.isTraining && '#74c365' }}
          onChange={(event, checked) => {
            handleChange(params.id, "isTraining", checked);
          }}
        />
      ),
    },
    {
      field: "notes",
      headerName: "Notes",
      width: 350,
      renderCell: (params: any) => (
        <TextField
          type="text"
          size="small"
          defaultValue={params.row.notes}
          style={{ width: "330px", height: "5px", margin: 5 }}
          onChange={(event: any) => {
            const updatedRows = updateRows(params.id, 'notes', event.target.value, workers);
            setWorkers(updatedRows);
          }}
          onBlur={async (event: any) => {
            handleChange(params.id, "notes", event.target.value);
          }}
          inputProps={{
            onKeyDown: (event) => {
              if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key)) {
                event.stopPropagation();
              }
              if (event.key === ' ') {
                event.stopPropagation();
              }
            },
          }}
        />
      ),
    },
  ];

  const getWorkers = async () => {
    const response = await getWorkersFromJob(state.user?.token, id, shiftData.rateTypeId, shiftData.projectId);
    if (response) {
      setWorkers(response);
      setOldWorkers(response);
      const orderNumbers: [] = response.map((worker: any) => worker.orderNumber);
      const maxOrderNumber =
        orderNumbers && orderNumbers.length > 0 ? Math.max(...orderNumbers) : 0;
      setBiggestOrderNumber(maxOrderNumber);
    }
  };

  const addNewWorker = async () => {
    setLoading(true);

    try {
      const newOrderNumber = biggestOrderNumber + 1;

      const response: any = await addNewWorkerToShift(state.user?.token, id, newOrderNumber);

      if (!response) {
        toast.error("Something went wrong!");
        return;
      }

      const newWorker = createEmptyWorker(response.data.userJobId, id, newOrderNumber);

      setWorkers((prev: any) => [...prev, newWorker]);
      setBiggestOrderNumber(newOrderNumber);
      toast.success("Added new worker successfully");

      const workerModel = await GetJobWithWorkersModel(state.user?.token, id);
      await connection.invoke("EditJob", `${week}-${year}`, workerModel);

    } catch (error) {
      console.error("Error adding new worker:", error);
      toast.error("Failed to add new worker");
    } finally {
      setLoading(false);
    }
  };

  const alignTimeOfWorkers = async () => {
    const response: any = await alignTimeOfWorkersWithJob(state.user?.token, id);
    if(response.success) {
      toast.success(`Changed ${response?.data.count} workers shift times!`)
    }
  };

  const updateWorkers = async () => {
    try {
      const response = await updateJobWorkerRows(state.user?.token, workers);

      const formattedDiffs = getFormattedDiffs(oldWorkers, workers);

      const changesJsonToString = JSON.stringify(formattedDiffs)
      await changeWorkerRowColorIdentifier(
        state.user?.token,
        selectedRows,
        selectedColor
      );

      if (response) {
        const response = await GetJobWithWorkersModel(state.user?.token, id);
        await saveJobVersionHistory(state.user?.token, id, changesJsonToString)

        await connection.invoke('EditJob', `${week}-${year}`, response);
      }
      getWorkers();
      if (response?.data) {
        toast.success('Updated Workers successfully')
        getWorkers();
      } else {
        toast.error('Something went wrong!')
      }
    } catch (error) {
      console.log(error);
    }
  };

  const Toolbar = () => {
    return (
      <div>
        <GridToolbarFilterButton />
      </div>
    );
  };

  const modifiedColumns = columns.map((col: any) => ({
    ...col,
    cellClassName: (params: any) => getBackColor(params.row.colorIdentifier),
  }));

  const handleNameChange = async (colId: any, newName: any, userId: any) => {
    setLoadingSpin(true)

    if (newName) {
      const isAlreadyAdded = workers.some(
        (row: any) => row.userId === newName.userId && row.userJobId !== colId
      );

      if (isAlreadyAdded) {
        toast.error(`${newName.fullName} is already added to this job.`);
        setLoadingSpin(false);
        return;
      }

      const updatedRows = workers.map((row: any) =>
        row.userJobId === colId
          ? {
            ...row,
            userId: newName.userId,
            fullName: newName.fullName,
            pts: newName.pts,
            phoneNumber: newName.phoneNumber,
            postCode: newName.postCode,
            hourlyRate: shiftTypeRates(shiftData.shiftType, newName),
            d: newName.isValidatedDriver,
            fatigueStatus: 3,
          }
          : row);
      setWorkers(updatedRows);

      const formattedDiffs = getFormattedDiffs(oldWorkers, updatedRows);
      const updatedRow = updatedRows.find((row: any) => row.userJobId === colId);
      const response: any = await updateJobWorkerRows(state.user?.token, [updatedRow]);

      if (response?.data?.data) toast.warning(response?.data?.message);

      if (Array.isArray(response?.data?.errors)) {
        toast.warning(response?.data?.errors[0])
      }

      if (response) {
        const doorPolicy = await doorToDoorPolicy(
          state.user?.token,
          colId,
          calculateHours(
            format(shiftData.startTime, "HH:mm"),
            format(shiftData.endTime, "HH:mm")
          )
        );

        if (doorPolicy.isOverLimit) {
          toast.error(doorPolicy.message)
          const updatedNotes = updatedRows.map((row: any) =>
            row.userJobId === colId
              ? {
                ...row,
                notes:
                  row.notes && row.notes !== "null"
                    ? row.notes.replace(/\(Door.*?m\)/g, "").trim() +
                    " " +
                    doorPolicy.message
                    : doorPolicy.message,
                colorIdentifier: "#f28519",
              }
              : row
          );
          setWorkers(updatedRows);
          const updatedRow = updatedNotes.find((row: any) => row.userJobId === colId);
          await updateJobWorkerRows(state.user?.token, [updatedRow]);
        } else {
          const updatedNotes = updatedRows.map((row: any) =>
            row.userJobId === colId
              ? {
                ...row,
                notes:
                  row.notes && row.notes !== "null"
                    ? row.notes.replace(/\(Door.*?m\)/g, "").trim()
                    : null,
                colorIdentifier: "white",
              }
              : row
          );
          setWorkers(updatedRows);
          const updatedRow = updatedNotes.find((row: any) => row.userJobId === colId);
          await updateJobWorkerRows(state.user?.token, [updatedRow]);
        }

        if (shiftData.jobStatus === 1) {
          await sendPushNotifications(state.user?.token, id, [newName.userId]);
        }
        setOldWorkers(workers);
        setTimeout(() => setLoadingSpin(false), 350);
        await saveChangesHistory(state.user?.token, id, formattedDiffs);
        const response = await GetJobWithWorkersModel(state.user?.token, id);
        await connection.invoke('EditJob', `${week}-${year}`, response);
      }
    } else {
      setOpenCancelReasonModal(true)
      setData({ userJobId: colId, workerId: userId })
      const updatedRows = workers.map((row: any) =>
        row.userJobId === colId
          ? {
            ...row,
            userId: null,
            fullName: null,
            pts: null,
            phoneNumber: null,
            postCode: null,
            hourlyRate: 0,
            colorIdentifier: null,
            s: 0,
            t: 0,
            notes: null,
            d: false,
            f: false,
            h_Color: 0,
            h: 0,
            paid: false,
            paymentMethod: null,
            paymentMethodId: null,
            riskAssessments: 0,
            fatigueStatus: 0,
            fatigueCumulative: null,
            fatigueDutyTiming: null,
            fatigueIndex: null,
            fatigueJobTypeAndBreaks: null,
            riskCumulative: null,
            riskDutyTiming: null,
            riskIndex: null,
            riskJobTypeAndBreaks: null,
            onDuty: null,
            offDuty: null,
            isTraining: false,
            hours: -1,
          }
          : row);
      setWorkers(updatedRows);

      const formattedDiffs = getFormattedDiffs(oldWorkers, updatedRows);

      const updatedRow = updatedRows.find((row: any) => row.userJobId === colId);
      const response = await updateJobWorkerRows(state.user?.token, [updatedRow]);

      if (response) {
        setTimeout(() => {
          setLoadingSpin(false);
        }, 350)
        setOldWorkers(workers);
        await saveChangesHistory(state.user?.token, id, formattedDiffs);
        const response = await GetJobWithWorkersModel(state.user?.token, id);
        await connection.invoke('EditJob', `${week}-${year}`, response);
      }
    }
  };

  useEffect(() => {
    const fetchSuggestions = async () => {
      try {
        const startTimeDate = parseISO(shiftData?.startTime);
        const endTimeDate = parseISO(shiftData?.endTime);
        const jobDateDate = parseISO(shiftData?.jobDate);

        const starTime = set(jobDateDate, {
          hours: startTimeDate.getHours(),
          minutes: startTimeDate.getMinutes(),
          seconds: startTimeDate.getSeconds(),
          milliseconds: 0,
        });

        const endTime = set(jobDateDate, {
          hours: endTimeDate.getHours(),
          minutes: endTimeDate.getMinutes(),
          seconds: endTimeDate.getSeconds(),
          milliseconds: 0,
        });

        const response = await GetWorkersSuggestions(
          state.user?.token,
          inputValue,
          format(starTime, 'yyyy-MM-dd HH:mm:ss'),
          format(endTime, 'yyyy-MM-dd HH:mm:ss')
        );

        if (response) {
          setSuggestions(response.data);
        } else {
          console.error("Error fetching suggestions");
        }
      } catch (error) {
        console.error("Error:", error);
      }
    };

    if (inputValue) {
      typingTimer = setTimeout(fetchSuggestions, typingDelay);
    }

    return () => clearTimeout(typingTimer);
  }, [inputValue]);

  const handleCellEditCommit = (params: any) => {
    console.log("Cell edit commit:", params);
    const updatedRows = workers.map((row: any) =>
      row.userJobId === params.id
        ? {
          ...row,
          workRole: params.workRole,
          notes: params.notes,
          hourlyRate: params.hourlyRate,
        }
        : row
    );
    setWorkers(updatedRows);
  };

  const handleRowSelection = (newSelectionModel: any) => {
    setSelectedRows(newSelectionModel);
  };

  const deleteWorker = async () => {
    const response = await removeWorkersFromJob(state.user?.token, selectedRows);
    if (response) {
      const response = await GetJobWithWorkersModel(state.user?.token, id);
      await connection.invoke('EditJob', `${week}-${year}`, response);
      toast.error('Deleted worker successfully.')
      setDeleteModalIsOpen(false);
    }
  };

  const handleChangeColor = async (event: any) => {
    setSelectedColor(event.target.value);
    const updatedRows = workers.map((row: any) =>
      selectedRows.includes(row.userJobId)
        ? { ...row, colorIdentifier: event.target.value }
        : row
    );
    setWorkers(updatedRows);

    const formattedDiffs = getFormattedDiffs(oldWorkers, updatedRows);

    const response = await updateJobWorkerRows(state.user?.token, updatedRows);
    if (response) {
      setOldWorkers(updatedRows);
      await saveChangesHistory(state.user?.token, id, formattedDiffs);
      const response = await GetJobWithWorkersModel(state.user?.token, id);
      await connection.invoke('EditJob', `${week}-${year}`, response);
    }
  };

  const handleCheck = async (checkType: string) => {
    if (!checkType) return;

    const isUncheck = checkType.startsWith("un");
    const type = isUncheck ? checkType.replace("un", "") : checkType;

    const updatedWorkers = workers.map((worker: any) => {
      if (!selectedRows.includes(worker.userJobId)) return worker;

      let value;

      if (type === CHECK_TYPES.PAID) {
        value = !isUncheck;
      } else if (isUncheck) {
        value = 0;
      } else if (type === CHECK_TYPES.MACRAIL) {
        return {
          ...worker,
          ['h_Color']: 1,
        };
      } else if (type === CHECK_TYPES.OTHER) {
        return {
          ...worker,
          ['h_Color']: 2,
        };
      } else {
        value = 1;
      }

      return {
        ...worker,
        [type]: value,
      };
    });

    setWorkers(updatedWorkers);

    const formattedDiffs = getFormattedDiffs(oldWorkers, updatedWorkers);

    const response = await updateJobWorkerRows(state.user?.token, updatedWorkers);
    if (response) {
      setOldWorkers(updatedWorkers);
      await saveChangesHistory(state.user?.token, id, formattedDiffs);
      const response = await GetJobWithWorkersModel(state.user?.token, id);
      await connection.invoke('EditJob', `${week}-${year}`, response);
    }
  };

  useEffect(() => {
    getWorkers();
    getSignedInOptions(state.user?.token).then(setSignedInOptions)
    getPaymentMethod(state.user?.token).then(setPaymentMethods);
    getWorkRolesSuggestionsV2(state.user?.token).then(setWorkRoleSuggestions);
    setTimeout(() => {
      setSuggestions([])
    }, 2000)
  }, [shiftData]);

  useEffect(() => {
    if (connection) {
      connection.on('EditJob', (jobModel: any) => {
        if (id === jobModel.job.id) {
          setWorkers(jobModel.workers)
          getWorkers();
        }
      })

      return () => {
        connection.off('EditJob');
      }
    }
  }, [connection])

  return (
    <>
      <AppModal
        title={`Are you sure you want to delete?`}
        centered
        open={deleteModalIsOpen}
        okButtonProps={{ danger: true }}
        cancelText={"NO"}
        okText={"YES"}
        onOk={deleteWorker}
        onCancel={() => setDeleteModalIsOpen(false)}
      />
      <Box sx={{ height: 578, width: "100%" }}>
        <DataGrid
          rows={workers}
          columns={modifiedColumns}
          slots={{ toolbar: Toolbar }}
          checkboxSelection
          disableRowSelectionOnClick
          getRowId={(row) => row.userJobId}
          processRowUpdate={handleCellEditCommit}
          onRowSelectionModelChange={handleRowSelection}
          sx={{
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: "",
              color: "",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
            },
            "& .red": {
              backgroundColor: "red",
            },
            "& .lightgreen": {
              backgroundColor: "lightgreen",
            },
            "& .yellow": {
              backgroundColor: "yellow",
            },
            "& .ember": {
              backgroundColor: '#f28519',
            },
            "& .blue": {
              backgroundColor: '#0096FF',
            },
            "& .pink": {
              backgroundColor: 'pink',
            },
            "& .black": {
              backgroundColor: 'black',
            },
          }}
        />
      </Box>
      <div className={styles.workerActions}>
        <div>
          <Button
            variant="contained"
            onClick={updateWorkers}
            disabled
            style={{
              margin: "20px",
              width: 160,
              height: 35,
              backgroundColor: loadingSpin ? "white" : "#156bc2",
              border: loadingSpin ? "1px solid green" : "none",
              color: loadingSpin ? "green" : "white",
              boxShadow: "none",
            }}
          >
            {loadingSpin ? <CircularProgress size={20} style={{ color: "green" }} /> : 'Saved Changes'}
          </Button>
          <Button
            variant="outlined"
            onClick={addNewWorker}
            startIcon={loading ? '' : <FontAwesomeIcon icon={faPlus} size={"3x"} color="#1976d2" />}
            disabled={loading}
            size="large"
            style={{
              width: '250px',
              borderColor: "#1976d2",
              color: "black",
              height: "35px",
              alignItems: 'center'
            }}
          >
            {loading ? <CircularProgress size={30} style={{ color: '#1976d2', fontSize: 25 }} /> : 'Add New Worker'}
          </Button>
        </div>
        <div>
          {selectedRows && selectedRows.length > 0 ? (
            <>
              Check all:
              <Select
                style={{ height: "35px", width: "100px", margin: "20px", textAlign: 'center' }}
                onChange={(e: any) => {
                  handleCheck(e.target.value);
                }}
              >
                <MenuItem style={{ height: 33, color: 'green', fontWeight: 600 }} disabled>Check</MenuItem>
                <MenuItem value={CHECK_TYPES.CONFIRMATION} style={{ height: 33 }}>T</MenuItem>
                <MenuItem value={CHECK_TYPES.SENTINEL} style={{ height: 33 }}>S</MenuItem>
                <MenuItem value={CHECK_TYPES.PAID} style={{ height: 33 }}>Paid</MenuItem>
                <MenuItem value={CHECK_TYPES.MACRAIL} style={{ height: 33 }}>H: Macrail</MenuItem>
                <MenuItem value={CHECK_TYPES.OTHER} style={{ height: 33, borderBottom: '1px solid orange' }}>H: Other</MenuItem>
                <MenuItem style={{ height: 33, fontWeight: 600 }} disabled>Uncheck</MenuItem>
                <MenuItem value={CHECK_TYPES.UNCONFIRMATION} style={{ height: 33 }}>T</MenuItem>
                <MenuItem value={CHECK_TYPES.UNSENTINEL} style={{ height: 33 }}>S</MenuItem>
                <MenuItem value={CHECK_TYPES.UNPAID} style={{ height: 33 }}>Unpaid</MenuItem>
                <MenuItem value={CHECK_TYPES.UNMACRAIL} style={{ height: 33 }}>H</MenuItem>
              </Select>
              Change Color:
              <Select
                labelId="demo-select-small-label"
                id="demo-select-small"
                defaultValue="white"
                style={{ height: "35px", width: "120px", margin: "20px" }}
                value={selectedColor}
                onChange={handleChangeColor}
              >
                <MenuItem value={"white"} style={{ height: 25 }}>
                  {" "}
                  No Color
                </MenuItem>
                <MenuItem
                  value={"lightgreen"}
                  style={{ backgroundColor: "#90ee90", height: 25 }}
                >
                  Green
                </MenuItem>
                <MenuItem
                  value={"red"}
                  style={{ backgroundColor: "#ff0000", height: 25 }}
                >
                  Red
                </MenuItem>
                <MenuItem
                  value={"yellow"}
                  style={{ backgroundColor: "#f7de3a", height: 25 }}
                >
                  Yellow
                </MenuItem>
                <MenuItem
                  value={"#f28519"}
                  style={{ backgroundColor: "#f28519", height: 25 }}
                >
                  Ember
                </MenuItem>
                <MenuItem
                  value={"#0096FF"}
                  style={{ backgroundColor: "#0096FF", height: 25 }}
                >
                  Blue
                </MenuItem>
                <MenuItem
                  value={"pink"}
                  style={{ backgroundColor: "pink", height: 25 }}
                >
                  Pink
                </MenuItem>
                <MenuItem
                  value={"black"}
                  style={{ backgroundColor: "black", height: 25, color: 'white' }}
                >
                  Black
                </MenuItem>
              </Select>
              <Button
                onClick={() => setDeleteModalIsOpen(true)}
                style={{
                  margin: "20px",
                  color: "white",
                  backgroundColor: "red",
                  height: "35px",
                  boxShadow: "none",
                }}
              >
                Delete
              </Button>
            </>
          ) : 
            <Button
              variant="contained"
              onClick={alignTimeOfWorkers}
              style={{
                margin: "20px",
                width: 160,
                height: 35,
                boxShadow: "none",
              }}
            >
              Align Time
            </Button>
          }
        </div>
      </div>
      <CancellationModal
        data={data}
        open={openCancelReasonModal}
        onClose={() => setOpenCancelReasonModal(false)}
      />
    </>
  );
};

export default Workers;
